/**
 * StatusTimeline — vertical history strip inside the dispatch popover.
 * Shows the en-route → on-site → done transitions of a single dispatch
 * with timestamps, GPS coords, and computed drive/on-site durations.
 *
 * Extracted from `pages/Dispatch.jsx` to slim the file (was 2,549 lines
 * before — this extract drops it by ~50 lines).
 */
import { Clock } from "lucide-react";
import { DISPATCH_STATUS_LABEL } from "./dispatchConstants";

const TIMELINE_DOTS = {
  en_route: "bg-purple-500",
  on_site: "bg-amber-500",
  done_for_day: "bg-slate-400",
  completed: "bg-green-500",
};

const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const diffMin = (a, b) => {
  if (!a || !b) return null;
  return Math.round((new Date(b) - new Date(a)) / 60000);
};

const fmtDuration = (mins) => {
  if (mins == null) return "";
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

export function StatusTimeline({ history }) {
  const enRoute = history.find(h => h.status === "en_route");
  const onSite = history.find(h => h.status === "on_site");
  const done = history.find(h => h.status === "done_for_day" || h.status === "completed");
  const driveMin = diffMin(enRoute?.at, onSite?.at);
  const onSiteMin = diffMin(onSite?.at, done?.at);

  return (
    <div className="border-t border-dashed border-slate-200 pt-1 space-y-0.5" data-testid="status-timeline">
      <div className="text-[10px] font-semibold text-slate-600 flex items-center gap-1">
        <Clock className="w-3 h-3 text-slate-400" />Timeline
        {driveMin != null && <span className="text-[8px] font-normal text-purple-500 ml-auto">Drive: {fmtDuration(driveMin)}</span>}
        {onSiteMin != null && <span className="text-[8px] font-normal text-amber-500 ml-1">On Site: {fmtDuration(onSiteMin)}</span>}
      </div>
      {history.map((h) => (
        <div key={`tl-${h.at}-${h.status}`} className="flex items-center gap-1.5 ml-1">
          <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${TIMELINE_DOTS[h.status] || "bg-slate-300"}`} />
          <span className="text-[9px] text-slate-600 font-medium w-16">{DISPATCH_STATUS_LABEL[h.status] || h.status}</span>
          <span className="text-[8px] text-slate-400">{formatTime(h.at)}</span>
          {h.lat && h.lng && <span className="text-[7px] text-slate-300 ml-auto">{h.lat.toFixed(3)}, {h.lng.toFixed(3)}</span>}
        </div>
      ))}
    </div>
  );
}
